import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useNavigate, useLocation } from 'react-router-dom';
import { getItem, getContainer, getItemByBarcode, lookupBarcode, createItem } from '../services/api';

const Scanner = () => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const isProcessingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get container context from navigation state
  const containerId = location.state?.containerId;
  const containerName = location.state?.containerName;

  const onScanSuccess = useCallback(async (decodedText, decodedResult) => {
    // Prevent processing multiple scans at once
    if (isProcessingRef.current) {
      console.log('Already processing a scan, ignoring...');
      return;
    }
    
    console.log('Scan result:', decodedText);
    isProcessingRef.current = true;
    setError(null);
    
    // Stop the scanner immediately to prevent duplicate scans
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
        scannerRef.current = null;
        setScanning(false);
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    
    try {
      // Determine if this is a UUID (QR code) or barcode
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(decodedText);
      
      if (isUUID) {
        // QR Code - try to find existing item or container in database
        let foundItem = null;
        let foundContainer = null;
        
        try {
          const itemResponse = await getItem(decodedText);
          foundItem = itemResponse.data;
        } catch (err) {
          // Not an item UUID
        }

        if (!foundItem) {
          try {
            const containerResponse = await getContainer(decodedText);
            foundContainer = containerResponse.data;
          } catch (err) {
            // Not a container UUID
          }
        }

        if (foundItem) {
          setResult({ type: 'item', data: foundItem });
          setTimeout(() => navigate(`/items/${foundItem.id}`), 2000);
        } else if (foundContainer) {
          setResult({ type: 'container', data: foundContainer });
          setTimeout(() => navigate(`/containers/${foundContainer.id}`), 2000);
        } else {
          setError('No item or container found with this QR code');
        }
      } else {
        // Barcode - behavior depends on context
        if (containerId) {
          // In container context: look up product info and create new item
          try {
            // Try to lookup product info (may fail if not found)
            let product = {};
            try {
              const lookupResponse = await lookupBarcode(decodedText);
              const apiResponse = lookupResponse.data;
              product = apiResponse.product || {};
            } catch (lookupErr) {
              console.log('Product not found in UPC database, creating item with barcode only');
              // Continue with empty product data
            }
            
            // Create item with product data (or just barcode if lookup failed)
            const newItemData = {
              name: product.title || `Item ${decodedText}`,
              description: product.description || '',
              barcode: decodedText,
              container_id: containerId,
              quantity: 1,
              product_brand: product.brand || null,
              product_category: product.category || null,
              upc_data: product.rawData ? JSON.stringify(product.rawData) : null
            };
            
            const createResponse = await createItem(newItemData);
            const newItem = createResponse.data;
            
            setResult({ type: 'item', data: newItem, created: true });
            setTimeout(() => navigate(`/items/${newItem.id}`), 2000);
          } catch (err) {
            console.error('Error creating item from barcode:', err);
            // Provide more detailed error information
            const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
            console.error('Detailed error:', errorMsg);
            setError(`Failed to create item: ${errorMsg}. You can add it manually.`);
          }
        } else {
          // No container context: try to find existing item by barcode
          try {
            const barcodeResponse = await getItemByBarcode(decodedText);
            const foundItem = barcodeResponse.data;
            setResult({ type: 'item', data: foundItem });
            setTimeout(() => navigate(`/items/${foundItem.id}`), 2000);
          } catch (err) {
            setError('No item found with this barcode. Scan from a container to create a new item.');
          }
        }
      }
    } catch (err) {
      console.error('Error processing scan:', err);
      setError('Error processing scan result');
    } finally {
      isProcessingRef.current = false;
    }
  }, [navigate, containerId]);

  const onScanError = useCallback((errorMessage) => {
    // Ignore scan errors (they're common during scanning)
    console.debug('Scan error:', errorMessage);
  }, []);

  useEffect(() => {
    let scanner = null;

    if (scanning) {
      scanner = new Html5QrcodeScanner(
        'qr-reader',
        { 
          fps: 10, 
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          formatsToSupport: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13], // All barcode formats
          videoConstraints: {
            facingMode: { exact: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            focusMode: { ideal: 'continuous' }
          },
          // Request high resolution for better clarity on iOS
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        false
      );

      scanner.render(onScanSuccess, onScanError);
      scannerRef.current = scanner;
    }

    return () => {
      if (scanner) {
        scanner.clear().catch(err => console.error('Error clearing scanner:', err));
      }
      scannerRef.current = null;
    };
  }, [scanning, onScanSuccess, onScanError]);

  const startScanning = () => {
    setScanning(true);
    setResult(null);
    setError(null);
    isProcessingRef.current = false;
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(err => console.error('Error clearing scanner:', err));
      scannerRef.current = null;
    }
    setScanning(false);
    setResult(null);
    setError(null);
    isProcessingRef.current = false;
  };

  return (
    <div className="container">
      <div className="card">
        <h1>Scan Barcode or QR Code</h1>
        
        {containerId && (
          <div className="card" style={{ marginBottom: '20px', backgroundColor: '#e7f3ff', padding: '10px' }}>
            <p style={{ margin: 0 }}>
              <strong>Scanning into:</strong> {containerName || 'Container'}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
              Barcodes will be added as new items
            </p>
          </div>
        )}
        
        {!scanning && (
          <button onClick={startScanning} className="button">
            Start Scanning
          </button>
        )}

        {scanning && (
          <>
            <div id="qr-reader" style={{ width: '100%' }}></div>
            <button onClick={stopScanning} className="button button-danger" style={{ marginTop: '20px' }}>
              Stop Scanning
            </button>
          </>
        )}

        {result && (
          <div className="card" style={{ marginTop: '20px', backgroundColor: '#d4edda' }}>
            <h3>{result.created ? 'Created' : 'Found'} {result.type}!</h3>
            <p><strong>Name:</strong> {result.data.name}</p>
            {result.data.description && <p><strong>Description:</strong> {result.data.description}</p>}
            {result.data.barcode && <p><strong>Barcode:</strong> {result.data.barcode}</p>}
            <p style={{ fontSize: '12px', marginTop: '10px' }}>Redirecting...</p>
          </div>
        )}

        {error && (
          <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8d7da', color: '#721c24' }}>
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Scanner;
